import React, { useState } from 'react'
import { Button, Checkbox, Tag, Modal, message } from 'antd'
import styles from './Dashboard.module.scss'
import DashboardImgs from '../../util/Images'
import axiosInstance from '../../api/axiosInstance'
import { DeleteOutlined } from '@ant-design/icons';
type StatusType = 'Approved' | 'In Review' | 'Rejected' | 'Approval Pending' | 'Ready for Approval' | 'Verified' | 'Inactive' | 'Promoted' | 'Published' | 'Ready for Tokenization' | 'Ready to Publish' | 'UnPublished'
type ListingType = 'Public' | 'Private'
type ButtonType = 'Applied for Paid Promotion' | 'Promote' | 'View Details' | 'Promotion Cancelled' | 'Others'

export interface AssetData {
  id: string | number // Add unique identifier
  productName: string
  category: string
  assetImage?: string
  totalSales: number
  transactions: number
  listing: ListingType
  status: StatusType
  buttonStatus: ButtonType
  //   checkAll? : boolean
  isChecked?: boolean // Track individual checkbox state
  rawState?: string // Raw state from DAM API
  marketplaceApprovedStatus?: any // Approved status object from marketplace API with currentStatus
  marketplaceData?: any // Full marketplace data object
}

interface AssetRowProps {
  asset: AssetData
  //   onCheckboxChange?: (checked: boolean) => void
  onCheckboxChange?: (id: string | number, checked: boolean) => void
  onChangeListingClick: (asset:any) => void
  onViewDetailsClick?: () => void
  onPromoteClick?: () => void
  onPublishClick: (id:any) => void
  onUnPublishClick: (id:any) => void
  publishLoading?: boolean
  unpublishLoading?: boolean
  onDeleteSuccess?: () => void
  onRefreshData?: () => Promise<void>
}

const statusColorMap: Record<StatusType, string> = {
  'Approved': 'green',
  'In Review': 'orange',
  'Rejected': 'red',
  'Approval Pending' : 'gold',
  'Ready for Approval' : 'blue',
  'Verified' : 'orange',
  'Inactive' : 'red',
  'Promoted' : 'blue',
  'Published' : 'blue',
  'Ready for Tokenization' : 'green',
  'Ready to Publish' : 'green',
  'UnPublished' : 'orange'

}

function AssetRow({
  asset,
  onCheckboxChange,
  onChangeListingClick,
  onViewDetailsClick,
  onPromoteClick,
  onPublishClick,
  publishLoading,
  onUnPublishClick,
  unpublishLoading,
  onDeleteSuccess,
  onRefreshData
}: AssetRowProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  
  const {
    id,
    productName,
    category,
    assetImage,
    totalSales,
    transactions,
    listing,
    status,
    buttonStatus,
    isChecked,
    rawState,
    marketplaceApprovedStatus,
    marketplaceData,
  } = asset

  const handleDeleteClick = () => {
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoading(true)
      
      // Delete from DAM API first
      await axiosInstance.delete(`dam/api/v1/digital-assets/${id}`)
      
      // Then delete from Marketplace API
      await axiosInstance.delete(`marketplace/api/v1/assets/${marketplaceData?._id}`)
      
      message.success('Asset deleted successfully')
      setShowDeleteModal(false)
      onDeleteSuccess?.()
      
      // Refetch the data
      await onRefreshData?.()
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || 'Failed to delete asset'
      message.error(errorMsg)
      console.error('Delete error:', error)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteModal(false)
  }

  return (
    <article className={styles.digital_asset}>
      <div className={styles.asset_left}>
        {/* <Checkbox
          checked={isChecked}
          onChange={(e) => onCheckboxChange?.(id, e.target.checked)}
        /> */}
        <div className={styles.asset_details}>
          <div className={styles.asset_img}>
            {assetImage ? <img src={assetImage } alt={productName} /> : <span className={styles.no_img}> Image <br/>Not <br/>Available</span> }
          </div>
          <div className={styles.asset_name}>
            <small>Product Name</small>
            <h4>{productName}</h4>
            <small>Category</small>
            <i>{category}</i>
          </div>
        </div>
      </div>
      <div className={styles.asset_right}>
        <div className={styles.analytic_card}>
          <small>Total Sales</small>
          <div className={styles.price}>$ {totalSales ? totalSales.toLocaleString() : 0}</div>
        </div>
        <div className={styles.analytic_card}>
          <small>Transaction</small>
          <div className={styles.transaction}>{transactions ? transactions : 0}</div>
        </div>
        <div className={styles.analytic_card}>
          <small>Listing</small>
          <div className={styles.listing_type}>
            <img
              src={marketplaceData?.visibility === 'public' ? DashboardImgs.publicImg : DashboardImgs.privateImg}
              alt={listing}
            />
            <span>{marketplaceData?.visibility}</span>
          </div>
          <aside
            className={`${styles.change_listing} ${rawState === 'SP_request_for_approval' ? styles.disabled : ''}`}
            onClick={()=>onChangeListingClick(asset)}
          >
            Change Listing
          </aside>
        </div>
        <div className={styles.status_card}>
          <small>Status</small>
          <div>
            <Tag color={statusColorMap[status]}>
              {status}
            </Tag>
          </div>
          {rawState !== 'DA_published' && (
            <div className={styles.btn_delete}>
              <Button 
                onClick={handleDeleteClick}
                loading={deleteLoading}
              >
                {/* <DeleteOutlined />  */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
                {/* <img src={DashboardImgs.deleteIcon} alt="Delete" /> */}
              </Button>
            </div>
          )}
          {rawState === 'DA_published' && <>
            <div
              className={
                buttonStatus === "Applied for Paid Promotion"
                  ? styles.btn_applied_status
                  : buttonStatus === "Promotion Cancelled"
                    ? styles.btn_promotion_cancel
                    : styles.btn_promote
              }

            >
              {buttonStatus === "Applied for Paid Promotion" ? (
                <>
                  <img
                    src={DashboardImgs.AwardBadge}
                    alt="badge"
                    className={styles.badge_icon}
                  />
                  <span className={styles.btn_status}>
                    Applied for Paid Promotion
                  </span>
                </>
              ) : buttonStatus === "Promotion Cancelled" ? (
                <span>Promotion Cancelled</span>
              ) : buttonStatus === "Others" ? (
                <span>Other</span>
              ) : (
                <span onClick={onPromoteClick}>Promote</span>
              )}
            </div>
            {/* <div className={styles.btn_unpublish} > 
            <Button onClick={() => onUnPublishClick(id)} loading={unpublishLoading}>Change Visibility</Button>
          </div>  */}
          </>
          }
        {rawState==="SP_ready_to_tokenise" && marketplaceApprovedStatus?.currentStatus === "approved" &&  <div className={styles.btn_publish} >
          <Button type='primary' onClick={() => onPublishClick(id)} loading={publishLoading}>Publish</Button>
        </div>
        }

        {/* <div className={styles.btn_view}>
            <span onClick={onViewDetailsClick}>View Details</span>
         </div> */}


      </div>
    </div>

      <Modal
        title={null}
        open={showDeleteModal}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLoading={deleteLoading}
        centered
        wrapClassName={styles.delete_confirm_modal}
        footer={
          <div className={styles.modal_footer_btns}>
            <Button key="cancel" onClick={handleDeleteCancel} className={styles.cancel_delete_btn}>
              Cancel
            </Button>
            <Button key="delete" type="primary" danger loading={deleteLoading} onClick={handleDeleteConfirm} className={styles.delete_confirm_btn}>
              Delete
            </Button>
          </div>
        }
      >
        <div className={styles.modal_icon_wrapper}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </div>
        <h2 className={styles.modal_title}>Are you sure?</h2>
        <p className={styles.modal_delete_message}>
          Do you really want to delete <b>{productName}</b>? <br />This process cannot be undone.
        </p>
      </Modal>
    </article>
  )
}

export default AssetRow